import { isEmpty } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { BillingAPI } from 'api/billing';
import { AddMembersRequestPayload } from 'api/billing/types';
import { UserType } from 'api/models';
import { PartnerCompany } from 'api/partner/types';
import { toastService } from 'components/molecules/toast';
import { InviteMemberFormPayload } from 'components/organisms/module-settings/team-members/invite-modal/form-v2';
import {
  PaymentStatuses,
  useManageAgentLicenses,
} from 'components/organisms/module-settings/team-members/manage-license';
import { MembersAPI } from 'data/members/api';
import { useBillingFeatureFlag } from 'hooks/use-account';
import { useTranslate } from 'hooks/use-translation';
import {
  SUBSCRIPTION_KEY,
  useAddPartnerMembersEstimateMutation,
  useForceSyncSubscriptionMutation,
  useSubscriptionQuery,
} from 'queries/billing';
import { ALL_MEMBERS_QUERY_KEY, ALL_USERS_QUERY_KEY } from 'queries/members';
import { ALL_PARTNER_COMPANIES_KEY } from 'queries/partner';

export enum InvitingStatus {
  Inviting = 'inviting',
  Invited = 'invited',
  Failed = 'failed',
}
interface Props {
  onInvited: () => void;
  partner: PartnerCompany;
}
interface Result {
  onInvite: (values: {
    payload: AddMembersRequestPayload;
    afterBillingConfirmation: boolean;
  }) => void;
  invitePaymentStatus?: PaymentStatuses;
  onInvitePaymentStatusClosed: () => void;
  isEstimateLoading: boolean;
  invitingStatus?: InvitingStatus;
  beforeInvite: (payload: InviteMemberFormPayload) => void;
}
export const useInviteAction = ({ onInvited, partner }: Props): Result => {
  const translate = useTranslate();
  const { data: subscription } = useSubscriptionQuery();
  const isBillingEnabled = useBillingFeatureFlag();
  const [invitingStatus, setInvitingStatus] = useState<InvitingStatus>();
  const [showPaymentStatusProgress, setShowPaymentStatusProgress] =
    useState(false);
  const queryClient = useQueryClient();
  const { mutateAsync: forceSyncSubscription } =
    useForceSyncSubscriptionMutation();

  const onInvite = useCallback(
    ({ payload, afterBillingConfirmation }) => {
      setInvitingStatus(InvitingStatus.Inviting);
      setShowPaymentStatusProgress(afterBillingConfirmation);
      (isBillingEnabled
        ? BillingAPI.addPartnerMembers(payload)
        : MembersAPI.inviteMembers(payload)
      )
        .then(async () => {
          toastService({
            title: translate('success'),
            message: translate('settings.team_members.invite.success', {
              number: payload.length,
            }),
          }).success();
          await queryClient.invalidateQueries(ALL_USERS_QUERY_KEY);
          await queryClient.invalidateQueries(ALL_MEMBERS_QUERY_KEY);
          await queryClient.invalidateQueries(ALL_PARTNER_COMPANIES_KEY);
          await queryClient.invalidateQueries(SUBSCRIPTION_KEY);
          if (subscription?.subscriptionId != null) {
            await forceSyncSubscription(subscription.subscriptionId);
          }
          setInvitingStatus(InvitingStatus.Invited);
          onInvited();
        })
        .catch(async (failedData) => {
          const failedUsers = failedData.filter(({ status }) => !status);

          if (isEmpty(failedUsers)) {
            toastService({
              title: translate('failure'),
              message: translate('settings.team_members.invite.error'),
            }).error();
            setInvitingStatus(InvitingStatus.Failed);
          } else {
            const emailsText = failedUsers
              .slice(0, 2)
              .map(({ user: { emailId } }) => emailId)
              .join(', ');
            const countText =
              failedUsers.length > 2
                ? '+'.concat(String(failedUsers.length - 2))
                : undefined;

            toastService({
              title: translate('settings.team_members.invite.partial.title'),
              message: `${emailsText} ${countText ?? ''}`,
            }).error();

            await queryClient.invalidateQueries(ALL_USERS_QUERY_KEY);
            await queryClient.invalidateQueries(ALL_MEMBERS_QUERY_KEY);
            setInvitingStatus(InvitingStatus.Failed);

            onInvited();
          }
        });
    },
    [
      forceSyncSubscription,
      isBillingEnabled,
      onInvited,
      queryClient,
      subscription?.subscriptionId,
      translate,
    ],
  );

  const { validateLicenseCount, isEstimateLoading } = useManageAgentLicenses({
    onLicenseCountValidated: onInvite,
    estimateMutation: useAddPartnerMembersEstimateMutation,
  });

  const beforeInvitingUsers = useCallback(
    ({ email: emails, role, permission }: InviteMemberFormPayload) => {
      const payload = emails.map(({ value }) => ({
        user: {
          emailId: value,
          userType: UserType.Partner,
          company: {
            companyId: partner.companyId,
          },
          role: {
            roleId: role?.value,
          },
        },
        permissionSet: {
          permissionSetType: permission?.value,
        },
      }));

      if (isBillingEnabled) {
        validateLicenseCount(payload);
      } else {
        onInvite({ payload });
      }
    },
    [isBillingEnabled, onInvite, partner.companyId, validateLicenseCount],
  );
  const onPaymentStatusClosed = useCallback(() => {
    setShowPaymentStatusProgress(false);
    setInvitingStatus(undefined);
  }, []);
  const paymentStatus = useMemo(() => {
    if (!showPaymentStatusProgress) {
      return undefined;
    }
    switch (invitingStatus) {
      case InvitingStatus.Inviting:
        return PaymentStatuses.Processing;
      case InvitingStatus.Invited:
        return PaymentStatuses.Processed;
      case InvitingStatus.Failed:
        return PaymentStatuses.ProcessingFailed;
      default:
        return undefined;
    }
  }, [invitingStatus, showPaymentStatusProgress]);

  return {
    beforeInvite: beforeInvitingUsers,
    invitingStatus,
    onInvite,
    isEstimateLoading,
    invitePaymentStatus: paymentStatus,
    onInvitePaymentStatusClosed: onPaymentStatusClosed,
  };
};
